import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Brain, Zap, TrendingUp, Activity, BarChart3, Target, Clock,
  RefreshCw, Loader2, CheckCircle2, PauseCircle, XCircle, SkipForward,
  Mail, Phone, Linkedin, MessageSquare, CalendarCheck, ListTodo, ChevronRight
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import api from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const DECISION_COLORS = {
  execute: '#10b981',
  skip: '#f59e0b',
  pause: '#f97316',
  exit: '#ef4444',
};

const STEP_TYPE_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];
const STEP_ICONS = { email: Mail, call: Phone, linkedin: Linkedin, sms: MessageSquare, meeting_request: CalendarCheck, task: ListTodo };
const HEALTH_COLORS = { excellent: 'text-emerald-500', good: 'text-blue-500', needs_improvement: 'text-amber-500', poor: 'text-red-500' };

export default function CadenceAnalytics() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const navigate = useNavigate();

  const loadAnalytics = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/api/brain/cadence-analytics/analytics?days=30');
      setData(res.data);
      setLastUpdated(new Date());
    } catch {
      if (!silent) toast.error('Failed to load analytics');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  // Live view: silent refresh every 60s + on window focus, so cadence
  // performance always reflects the Brain's latest executions.
  useEffect(() => {
    const t = setInterval(() => loadAnalytics(true), 60000);
    const onFocus = () => loadAnalytics(true);
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [loadAnalytics]);

  const loadBrainInsights = async () => {
    setInsightsLoading(true);
    try {
      const res = await api.post('/api/brain/cadence-analytics/analytics/brain-insights');
      setInsights(res.data);
      toast.success('Brain insights generated');
    } catch {
      toast.error('Failed to generate insights');
    } finally {
      setInsightsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="cadence-analytics-loading">
        <div className="flex items-center gap-3">
          <Brain className="h-6 w-6 text-violet-500 animate-pulse" />
          <h1 className="text-2xl font-semibold tracking-tight">Cadence Analytics</h1>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[1,2,3,4].map(i => (
            <Card key={i}><CardContent className="p-6"><div className="animate-pulse space-y-2"><div className="h-4 bg-muted rounded w-1/2" /><div className="h-8 bg-muted rounded w-3/4" /></div></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { overview, brain_decisions, step_effectiveness, top_cadences, recent_brain_activity } = data;

  const decisionChartData = Object.entries(brain_decisions.distribution)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, color: DECISION_COLORS[k] }));

  const stepChartData = Object.entries(step_effectiveness).map(([type, stats], i) => ({
    name: type.charAt(0).toUpperCase() + type.slice(1),
    ai_executed: stats.ai_executed,
    manual: stats.manual,
    pending: stats.total - stats.ai_executed - stats.manual,
    fill: STEP_TYPE_COLORS[i % STEP_TYPE_COLORS.length],
  }));

  return (
    <div className="p-6 space-y-6" data-testid="cadence-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-violet-500/15 flex items-center justify-center">
            <Brain className="h-5 w-5 text-violet-500" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Cadence Performance</h1>
            <p className="text-sm text-muted-foreground">AI Brain autonomous follow-up analytics — Last {data.period_days} days</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadBrainInsights} disabled={insightsLoading} data-testid="get-brain-insights-btn">
            {insightsLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Brain className="h-4 w-4 mr-2" />}
            Brain Insights
          </Button>
          <Button variant="ghost" size="icon" onClick={() => loadAnalytics()} data-testid="refresh-analytics-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
          {lastUpdated && (
            <span className="text-[11px] text-muted-foreground" data-testid="analytics-last-updated">
              Updated {lastUpdated.toLocaleTimeString()} · auto-refreshes
            </span>
          )}
        </div>
      </div>

      {/* Brain Insights Panel (when available) */}
      {insights && <BrainInsightsPanel insights={insights} />}

      {/* Empty-state — no cadences run yet (Iter 59c) */}
      {overview.total_cadences === 0 && (
        <Card data-testid="cadence-analytics-empty">
          <CardContent className="py-10 text-center">
            <Brain className="h-10 w-10 text-violet-500/30 mx-auto mb-3" />
            <p className="text-sm font-medium">No cadence runs yet</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
              Cadences auto-generate when you complete onboarding. If your leads are Bronze (<code className="px-1 py-0.5 rounded bg-amber-500/10 text-amber-600 text-[10px]">needs_review</code>), open them from the Pipeline and approve to start sending — analytics populate after the first send.
            </p>
            <Button size="sm" variant="outline" className="mt-4" onClick={() => window.location.href = '/leads'} data-testid="cadence-analytics-go-pipeline-btn">
              Go to Pipeline
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Overview Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard icon={Target} label="Total Cadences" value={overview.total_cadences}
          sub={`${overview.active} active`} color="text-violet-500" testId="stat-total-cadences" />
        <StatCard icon={CheckCircle2} label="Completion Rate" value={`${overview.completion_rate}%`}
          sub={`${overview.completed} completed`} color="text-emerald-500" testId="stat-completion-rate" />
        <StatCard icon={Zap} label="Automation Rate" value={`${overview.automation_rate}%`}
          sub={`${overview.ai_executed_steps} AI steps`} color="text-blue-500" testId="stat-automation-rate" />
        <StatCard icon={Clock} label="Avg Duration" value={`${overview.avg_duration_days}d`}
          sub={`${brain_decisions.total} brain decisions`} color="text-amber-500" testId="stat-avg-duration" />
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Brain Decision Distribution */}
        <Card data-testid="decision-distribution-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Brain className="h-4 w-4 text-violet-500" />
              Brain Decision Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {decisionChartData.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={180}>
                  <PieChart>
                    <Pie data={decisionChartData} cx="50%" cy="50%" innerRadius={45} outerRadius={70}
                      paddingAngle={3} dataKey="value" strokeWidth={0}>
                      {decisionChartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 flex-1">
                  {decisionChartData.map(d => (
                    <div key={d.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                        <span>{d.name}</span>
                      </div>
                      <span className="font-medium">{d.value}</span>
                    </div>
                  ))}
                  <div className="pt-2 border-t text-xs text-muted-foreground">
                    Avg Confidence: <span className="font-medium text-foreground">{brain_decisions.avg_confidence}</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No brain decisions yet. Generate cadences and run the Brain to see data.</p>
            )}
          </CardContent>
        </Card>

        {/* Step Type Effectiveness */}
        <Card data-testid="step-effectiveness-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-500" />
              Step Type Effectiveness
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stepChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={stepChartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} className="text-muted-foreground" />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Bar dataKey="ai_executed" name="AI Executed" fill="#8b5cf6" radius={[2,2,0,0]} />
                  <Bar dataKey="manual" name="Manual" fill="#3b82f6" radius={[2,2,0,0]} />
                  <Bar dataKey="pending" name="Pending" fill="#d4d4d8" radius={[2,2,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No step data available yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row: Top Cadences + Recent Brain Activity */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Top Cadences */}
        <Card data-testid="top-cadences-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              Top Cadences by Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_cadences.length > 0 ? (
              <div className="space-y-2">
                {top_cadences.map(c => (
                  <div key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/leads/${c.id}`)} data-testid={`top-cadence-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.cadence_name}</p>
                      <p className="text-xs text-muted-foreground">{c.lead_name}</p>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${
                      c.status === 'active' ? 'text-emerald-500 border-emerald-500/30' :
                      c.status === 'completed' ? 'text-blue-500 border-blue-500/30' :
                      'text-amber-500 border-amber-500/30'
                    }`}>{c.status}</Badge>
                    <div className="w-20">
                      <Progress value={c.progress_pct} className="h-1.5" />
                      <p className="text-[10px] text-muted-foreground text-right mt-0.5">{c.current_step}/{c.total_steps}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No cadences found. Generate cadences from lead details pages.</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Brain Activity */}
        <Card data-testid="recent-brain-activity">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4 text-violet-500" />
              Recent Brain Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recent_brain_activity.length > 0 ? (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {recent_brain_activity.map((a, i) => {
                  const decision = a.decision || 'execute';
                  const DecisionIcon = decision === 'execute' ? CheckCircle2 : decision === 'skip' ? SkipForward : decision === 'pause' ? PauseCircle : XCircle;
                  return (
                    <div key={i} className="flex gap-2 p-2 rounded-lg bg-muted/30" data-testid={`brain-activity-${i}`}>
                      <DecisionIcon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: DECISION_COLORS[decision] }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs">{a.summary}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-[10px]" style={{ color: DECISION_COLORS[decision], borderColor: DECISION_COLORS[decision] + '40' }}>
                            {decision}
                          </Badge>
                          {a.confidence && (
                            <span className="text-[10px] text-muted-foreground">{Math.round(a.confidence * 100)}% conf</span>
                          )}
                          {a.step_type && (
                            <span className="text-[10px] text-muted-foreground">{a.step_type}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No brain cadence activity yet. Run the Brain executor to see decisions.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-muted-foreground">{label}</p>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
      </CardContent>
    </Card>
  );
}

function BrainInsightsPanel({ insights }) {
  const healthColor = HEALTH_COLORS[insights.health_assessment] || 'text-muted-foreground';
  return (
    <Card className="border-violet-500/20 bg-violet-500/[0.02]" data-testid="brain-insights-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Brain className="h-4 w-4 text-violet-500" />
            Brain Analysis
          </CardTitle>
          <Badge variant="outline" className={`text-xs ${healthColor}`}>
            {(insights.health_assessment || '').replace('_', ' ')}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm">{insights.summary}</p>

        {insights.insights?.length > 0 && (
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {insights.insights.map((ins, i) => (
              <div key={i} className={`p-3 rounded-lg border ${
                ins.type === 'positive' ? 'border-emerald-500/20 bg-emerald-500/5' :
                ins.type === 'warning' ? 'border-amber-500/20 bg-amber-500/5' :
                'border-red-500/20 bg-red-500/5'
              }`} data-testid={`insight-${i}`}>
                <p className="text-xs font-medium">{ins.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{ins.description}</p>
              </div>
            ))}
          </div>
        )}

        {insights.recommendations?.length > 0 && (
          <div>
            <p className="text-xs font-medium mb-1.5">Recommendations</p>
            {insights.recommendations.map((r, i) => (
              <p key={i} className="text-xs text-muted-foreground flex items-start gap-1.5 mb-1">
                <ChevronRight className="h-3 w-3 mt-0.5 flex-shrink-0 text-violet-500" />{r}
              </p>
            ))}
          </div>
        )}

        {insights.optimal_cadence_profile && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t">
            <div><p className="text-[10px] text-muted-foreground">Ideal Steps</p><p className="text-sm font-medium">{insights.optimal_cadence_profile.ideal_step_count}</p></div>
            <div><p className="text-[10px] text-muted-foreground">Best Types</p><p className="text-sm font-medium">{insights.optimal_cadence_profile.best_performing_step_types?.join(', ')}</p></div>
            <div><p className="text-[10px] text-muted-foreground">Duration</p><p className="text-sm font-medium">{insights.optimal_cadence_profile.ideal_duration_days}d</p></div>
            <div><p className="text-[10px] text-muted-foreground">Success Factor</p><p className="text-sm font-medium truncate">{insights.optimal_cadence_profile.key_success_factor}</p></div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
